import { useAuth } from '@clerk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { DentistPracticeOption } from './dentistPractices';

export function useDentistPractices() {
    const { isLoaded, isSignedIn, sessionId, getToken } = useAuth();
    const isAdmin = isLoaded && isSignedIn === true;
    const queryClient = useQueryClient();
    const query = useQuery<DentistPracticeOption[]>({
        queryKey: ['dentist-practices', sessionId],
        enabled: isAdmin,
        staleTime: Infinity,
        retry: false,
        queryFn: async ({ signal }) => {
            const token = await getToken();
            if (signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
            if (!token) throw new Error('Sign in again to search dentists and practices.');
            const response = await fetch('/.netlify/functions/dentist-practices', {
                headers: { Authorization: `Bearer ${token}` },
                signal,
                cache: 'no-store',
            });
            if (!response.ok) {
                throw new Error(response.status === 401
                    ? 'Sign in again to search dentists and practices.'
                    : 'Unable to load dentists and practices. Try again or enter the details manually.');
            }
            return response.json();
        },
    });

    useEffect(() => {
        if (!sessionId) return;
        return () => {
            const filters = { queryKey: ['dentist-practices', sessionId], exact: true };
            void queryClient.cancelQueries(filters);
            queryClient.removeQueries(filters);
        };
    }, [queryClient, sessionId]);

    return {
        isAdmin,
        isLoaded,
        sessionId,
        options: isAdmin ? query.data ?? [] : [],
        isLoading: query.isInitialLoading,
        error: query.error instanceof Error ? query.error.message : null,
        retry: () => { void query.refetch(); },
    };
}
